
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useUpdatePartner, useDeletePartner, CreatePartnerData } from "@/hooks/use-partners";
import { useContacts } from "@/hooks/use-contacts";
import { useActivitiesByPartner } from "@/hooks/use-activities";
import { ActivitiesTab } from "./ActivitiesTab";
import { Users, Trash2, UserCircle, Calendar } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const partnerSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().optional(),
  partner_type: z.enum(["Affiliate", "Associate", "Reseller"]),
  status: z.enum(["Signed", "Not-Signed", "Informal"]),
  agreement_signed: z.boolean(),
  commission_rate: z.number().min(0).max(100),
  notes: z.string().optional(),
});

interface PartnerEditModalProps {
  partner: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PartnerEditModal = ({ partner, open, onOpenChange }: PartnerEditModalProps) => {
  const updatePartner = useUpdatePartner();
  const deletePartner = useDeletePartner();
  const { data: contacts = [] } = useContacts();
  const { data: activities = [], isLoading: activitiesLoading } = useActivitiesByPartner(partner.id);

  // Filter contacts that are assigned to this partner
  const partnerContacts = contacts.filter(contact => contact.external_owner_id === partner.id);

  const form = useForm({
    resolver: zodResolver(partnerSchema),
    defaultValues: {
      first_name: partner.first_name || "",
      last_name: partner.last_name || "",
      email: partner.email || "",
      phone: partner.phone || "",
      partner_type: partner.partner_type || "Affiliate",
      status: partner.status || "Not-Signed",
      agreement_signed: partner.agreement_signed || false,
      commission_rate: partner.commission_rate || 10,
      notes: partner.notes || "",
    },
  });

  const onSubmit = async (data: any) => {
    // Clean up the data to ensure proper types
    const cleanedData = {
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      phone: data.phone || null,
      partner_type: data.partner_type,
      status: data.status,
      agreement_signed: data.agreement_signed,
      commission_rate: data.commission_rate,
      notes: data.notes || null,
    };

    updatePartner.mutate({ id: partner.id, data: cleanedData }, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  const handleDelete = () => {
    deletePartner.mutate(partner.id, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="bg-slate-800 -mx-6 -mt-6 px-6 py-4 rounded-t-xl">
          <DialogTitle className="text-slate-50 flex items-center gap-2">
            <UserCircle className="h-5 w-5" />
            Edit Partner
          </DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="contacts">
              <Users className="h-4 w-4 mr-2" />
              Contacts ({partnerContacts.length})
            </TabsTrigger>
            <TabsTrigger value="activities">
              <Calendar className="h-4 w-4 mr-2" />
              Activities ({activities.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="details" className="flex-1 overflow-auto">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 p-6">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="first_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="last_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name *</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email *</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="grid grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="partner_type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Partner Type</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Affiliate">Affiliate</SelectItem>
                            <SelectItem value="Associate">Associate</SelectItem>
                            <SelectItem value="Reseller">Reseller</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                          <SelectItem value="Signed">Signed</SelectItem>
                          <SelectItem value="Not-Signed">Not-Signed</SelectItem>
                          <SelectItem value="Informal">Informal</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="commission_rate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Commission Rate</FormLabel>
                        <Select onValueChange={(value) => field.onChange(parseInt(value))} value={field.value?.toString()}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select rate" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="6">6%</SelectItem>
                            <SelectItem value="8">8%</SelectItem>
                            <SelectItem value="10">10%</SelectItem>
                            <SelectItem value="12">12%</SelectItem>
                            <SelectItem value="15">15%</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                
                <FormField
                  control={form.control}
                  name="agreement_signed"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                      <div className="space-y-0.5">
                        <FormLabel>Agreement Signed</FormLabel>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Details & Notes</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Additional details and notes about the partner" className="min-h-[100px]" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <div className="flex justify-between">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button type="button" variant="destructive">
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Partner
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Partner</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this partner? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                          onClick={handleDelete}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  
                  <div className="space-x-2">
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={updatePartner.isPending}
                      className="bg-[#DFEF97] text-black hover:bg-[#D4E68F]"
                    >
                      {updatePartner.isPending ? "Updating..." : "Update Partner"}
                    </Button>
                  </div>
                </div>
              </form>
            </Form>
          </TabsContent>
          
          <TabsContent value="contacts" className="flex-1 overflow-auto">
            <div className="space-y-4 p-6">
              <h3 className="text-lg font-semibold">Contacts assigned to {partner.first_name} {partner.last_name}</h3>
              {partnerContacts.length === 0 ? (
                <p className="text-muted-foreground">No contacts assigned to this partner.</p>
              ) : (
                <div className="space-y-2">
                  {partnerContacts.map((contact) => (
                    <div key={contact.id} className="p-3 border rounded-lg">
                      <div className="font-medium">{contact.first_name} {contact.last_name}</div>
                      <div className="text-sm text-muted-foreground">{contact.email}</div>
                      {contact.job_title && (
                        <div className="text-sm text-muted-foreground">{contact.job_title}</div>
                      )}
                      {contact.organizations && (
                        <div className="text-sm text-muted-foreground">
                          Company: {contact.organizations.name}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="activities" className="flex-1 overflow-auto">
            <ActivitiesTab 
              activities={activities}
              isLoading={activitiesLoading}
              partnerId={partner.id}
              title="Partner Activities"
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
